import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiHeart, FiUsers, FiTrendingUp, FiAward, FiBriefcase, FiMapPin, FiClock, FiDollarSign, FiArrowRight, FiSend, FiShield, FiStar, FiPhone } = FiIcons;

const Careers = () => {
  const benefits = [
    {
      icon: FiHeart,
      title: "Meaningful Work",
      description: "Make a real difference in people's lives every day through compassionate care."
    },
    {
      icon: FiTrendingUp,
      title: "Career Development",
      description: "Continuous training opportunities and clear career progression pathways."
    },
    {
      icon: FiAward,
      title: "Competitive Benefits",
      description: "Competitive salary, pension scheme, and comprehensive benefits package."
    },
    {
      icon: FiUsers,
      title: "Supportive Team",
      description: "Join a caring team that supports each other and celebrates achievements."
    }
  ];

  const applicationProcess = [
    {
      step: "1",
      title: "Apply Online",
      description: "Submit your application through our online portal or send your CV directly to us."
    },
    {
      step: "2",
      title: "Initial Screening",
      description: "We'll review your application and conduct an initial phone screening."
    },
    {
      step: "3",
      title: "Interview",
      description: "Face-to-face or video interview to discuss your experience and motivations."
    },
    {
      step: "4",
      title: "Checks & Training",
      description: "DBS checks, references, and comprehensive induction training."
    },
    {
      step: "5",
      title: "Start Your Career",
      description: "Begin your rewarding career with ongoing support and development."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Careers at Odara Care - Join Our Healthcare Team | Care Jobs UK</title>
        <meta name="description" content="Join Odara Care's compassionate healthcare team. Explore live vacancies for care assistants, live-in carers, and specialist positions." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-900 to-secondary-900 py-24 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">Join Our Care Family</h1>
            <p className="text-xl text-primary-100 max-w-3xl mx-auto leading-relaxed">
              Build a meaningful career in healthcare with Odara Care. We're looking for compassionate individuals who want to make a real difference in people's lives every day.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why Join Us Quick Stats */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="flex items-center space-x-4 p-6 bg-primary-50 rounded-2xl">
              <div className="w-12 h-12 bg-primary-900 rounded-xl flex items-center justify-center flex-shrink-0">
                <SafeIcon icon={FiHeart} className="text-white w-6 h-6" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Compassionate Team</h3>
                <p className="text-sm text-gray-600">Work with people who truly care.</p>
              </div>
            </div>
            <div className="flex items-center space-x-4 p-6 bg-secondary-50 rounded-2xl">
              <div className="w-12 h-12 bg-secondary-900 rounded-xl flex items-center justify-center flex-shrink-0">
                <SafeIcon icon={FiStar} className="text-white w-6 h-6" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Career Growth</h3>
                <p className="text-sm text-gray-600">Training and development pathways.</p>
              </div>
            </div>
            <div className="flex items-center space-x-4 p-6 bg-primary-50 rounded-2xl">
              <div className="w-12 h-12 bg-primary-900 rounded-xl flex items-center justify-center flex-shrink-0">
                <SafeIcon icon={FiShield} className="text-white w-6 h-6" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Stable Careers</h3>
                <p className="text-sm text-gray-600">Competitive pay and job security.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Live Vacancies Widget Section */}
      <section id="vacancies" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Current Vacancies</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Browse our live roles and apply directly through our secure portal.
            </p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-200"
          >
            <iframe
              src="https://day2day.care/careers/odara-care/widget"
              width="100%"
              height="600"
              frameBorder="0"
              style={{ border: 0, width: '100%', height: '800px' }}
            ></iframe>
          </motion.div>
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Work With Odara Care?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We believe in investing in our people and creating an environment where you can thrive both personally and professionally.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-primary-50 to-secondary-50 p-8 rounded-xl hover:shadow-lg transition-shadow duration-300 text-center border border-primary-100"
              >
                <div className="w-16 h-16 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={benefit.icon} className="w-8 h-8 text-primary-900" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Process */}
      <section className="py-20 bg-gradient-to-br from-primary-50 to-secondary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">How to Apply</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our straightforward process is designed to help us get to know you and ensure we're the right fit for each other.
            </p>
          </motion.div>

          <div className="space-y-8 max-w-4xl mx-auto">
            {applicationProcess.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'} gap-8`}
              >
                <div className="flex-1">
                  <div className={`bg-white p-8 rounded-xl shadow-sm border border-primary-100 ${index % 2 === 0 ? 'text-left' : 'text-right'}`}>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{step.title}</h3>
                    <p className="text-lg text-gray-600 leading-relaxed">{step.description}</p>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-primary-900 rounded-full flex items-center justify-center shadow-lg">
                    <span className="text-2xl font-bold text-white">{step.step}</span>
                  </div>
                </div>
                <div className="flex-1 hidden md:block"></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-900 to-secondary-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <h2 className="text-4xl font-bold text-white mb-4">Ready to Start Your Care Career?</h2>
            <p className="text-xl text-primary-100 max-w-3xl mx-auto">
              Join our team of dedicated healthcare professionals and make a meaningful difference in people's lives every day.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="mailto:careers@odaracare.org.uk" 
                className="inline-flex items-center justify-center bg-white text-primary-900 px-8 py-4 rounded-full text-lg font-semibold hover:bg-primary-50 transition-colors duration-200"
              >
                <SafeIcon icon={FiSend} className="mr-2 w-5 h-5" /> Send Your CV
              </a>
              <a 
                href="tel:03333394077" 
                className="inline-flex items-center justify-center border-2 border-white text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white hover:text-primary-900 transition-colors duration-200"
              >
                <SafeIcon icon={FiPhone} className="mr-2 w-5 h-5" /> Call to Discuss
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Careers;